import { useMemo } from 'react';

// ExcelLayout 型（APIレスポンス）
export interface ExcelLayoutCell {
  cell: string;
  row: number;
  col: number;
  value: string;
  isInput: boolean;
  isYellow: boolean;
  style?: {
    bold?: boolean;
    fontSize?: number;
    italic?: boolean;
    color?: string;
    bgColor?: string;
    align?: string;
    valign?: string;
    wrap?: boolean;
  };
  label?: string;
  fieldType?: string;
}

export interface ExcelLayout {
  sheetName: string;
  rows: number;
  cols: number;
  mergedCells: Array<{ start: string; end: string; startRow: number; startCol: number; endRow: number; endCol: number }>;
  cells: ExcelLayoutCell[];
  colWidths: number[];
  rowHeights: number[];
}

export interface ExcelField {
  id: string;
  cell_ref: string;
  merge_range?: string | null;
  sheet_name?: string;
  label?: string;
  field_type?: string;
  required?: boolean;
}

interface Props {
  layout: ExcelLayout;
  fields: ExcelField[];
  fieldValues: Record<string, string>;
  resolvedValues: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
}

export function ExcelSheetViewer({ layout, fields, fieldValues, resolvedValues, onFieldChange }: Props) {
  // cell_ref (シート接頭辞除去) → field のマップ
  const fieldMap = useMemo(() => {
    const map = new Map<string, ExcelField>();
    for (const f of fields) {
      const ref = f.cell_ref.includes('!') ? f.cell_ref.split('!')[1] : f.cell_ref;
      map.set(ref, f);
      if (f.merge_range) {
        const mr = f.merge_range.includes('!') ? f.merge_range.split('!')[1] : f.merge_range;
        map.set(mr.split(':')[0], f);
      }
    }
    return map;
  }, [fields]);

  // セル位置マップ
  const cellMap = useMemo(() => {
    const map = new Map<string, ExcelLayoutCell>();
    for (const c of layout.cells) map.set(`${c.row}-${c.col}`, c);
    return map;
  }, [layout.cells]);

  // 結合セル
  const { spans, skip } = useMemo(() => {
    const spans = new Map<string, { rowSpan: number; colSpan: number }>();
    const skip = new Set<string>();
    for (const m of layout.mergedCells) {
      spans.set(`${m.startRow}-${m.startCol}`, { rowSpan: m.endRow - m.startRow + 1, colSpan: m.endCol - m.startCol + 1 });
      for (let r = m.startRow; r <= m.endRow; r++) {
        for (let c = m.startCol; c <= m.endCol; c++) {
          if (r !== m.startRow || c !== m.startCol) skip.add(`${r}-${c}`);
        }
      }
    }
    return { spans, skip };
  }, [layout.mergedCells]);

  // 可視行（rowHeight > 0）
  const usedRows = useMemo(() => {
    const rows: number[] = [];
    for (let r = 1; r <= layout.rows; r++) {
      if ((layout.rowHeights[r - 1] || 0) > 0) rows.push(r);
    }
    return rows;
  }, [layout.rows, layout.rowHeights]);

  // テーブル合計幅
  const tableWidth = useMemo(() => layout.colWidths.reduce((a, b) => a + b, 0), [layout.colWidths]);

  return (
    <div style={{ overflow: 'auto', padding: 8, background: '#fafafa', border: '1px solid var(--border)', borderRadius: 4 }}>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth, background: '#fff' }}>
        <colgroup>
          {layout.colWidths.map((w, i) => (
            <col key={i} style={{ width: w }} />
          ))}
        </colgroup>
        <tbody>
          {usedRows.map(rowIdx => {
            const rh = layout.rowHeights[rowIdx - 1] || 20;
            return (
              <tr key={rowIdx} style={{ height: rh }}>
                {Array.from({ length: layout.cols }, (_, i) => i + 1).map(colIdx => {
                  const key = `${rowIdx}-${colIdx}`;
                  if (skip.has(key)) return null;
                  const span = spans.get(key);
                  const cellData = cellMap.get(key);
                  const st = cellData?.style || {};
                  const val = cellData?.value || '';

                  // このセルが入力フィールドか判定
                  const field = cellData ? fieldMap.get(cellData.cell) : undefined;
                  const isField = !!field && (cellData?.isInput || cellData?.isYellow);
                  const fieldId = field?.id;
                  const resolvedVal = fieldId ? resolvedValues[fieldId] : undefined;
                  const manualVal = fieldId ? (fieldValues[fieldId] ?? '') : '';
                  const hasResolved = !!resolvedVal;
                  const isCheckbox = field?.field_type === 'checkbox';

                  // field未登録だが value が純粋にチェック記号のセル → 擬似チェックボックス
                  const trimmedVal = val.trim();
                  const isAutoCheckbox = !isField && cellData && trimmedVal.length <= 2 && /^[☐□☑✓✔þ¨]+$/.test(trimmedVal);
                  const autoCheckboxId = isAutoCheckbox && cellData ? `cell:${cellData.cell}` : undefined;
                  const autoCheckboxStored = autoCheckboxId ? fieldValues[autoCheckboxId] : undefined;
                  const autoCheckboxChecked = autoCheckboxStored !== undefined
                    ? (autoCheckboxStored === '1' || autoCheckboxStored === '☑')
                    : /[☑✓✔þ]/.test(trimmedVal);

                  // 背景色
                  let bg: string = 'transparent';
                  if (isField && hasResolved) bg = '#e8f5e9';
                  else if (isField && isCheckbox) bg = '#fffde7';
                  else if (isField) bg = '#fffde7';
                  else if (isAutoCheckbox) bg = '#fffde7';
                  else if (st.bgColor) bg = st.bgColor as string;

                  // 結合セル（通常セル用の挙動調整）
                  const isMerged = !!(span && (span.rowSpan > 1 || span.colSpan > 1));
                  const hasWrap = st.wrap || isMerged;

                  // 長文セルが右隣の空セルにオーバーフローできるか判定（Excelの標準挙動）
                  const nextKey = `${rowIdx}-${colIdx + 1}`;
                  const nextCell = cellMap.get(nextKey);
                  const nextIsEmpty = !nextCell || !nextCell.value;
                  const canOverflow = !isField && val.length > 0 && !hasWrap && nextIsEmpty;

                  return (
                    <td key={colIdx}
                      rowSpan={span?.rowSpan} colSpan={span?.colSpan}
                      style={{
                        border: '0.5px solid #ddd',
                        padding: isField || isAutoCheckbox ? 0 : '1px 3px',
                        background: bg,
                        fontWeight: st.bold ? 700 : 400,
                        fontSize: st.fontSize ? `${st.fontSize}pt` : '10pt',
                        fontStyle: st.italic ? 'italic' : undefined,
                        color: (st.color as string) || '#000',
                        textAlign: (st.align as 'left' | 'center' | 'right') || undefined,
                        verticalAlign: (st.valign as string) || 'middle',
                        whiteSpace: hasWrap ? 'normal' : 'nowrap',
                        overflow: isField ? 'hidden' : 'visible',
                        lineHeight: 1.3,
                        position: 'relative',
                        zIndex: canOverflow ? 2 : undefined,
                      }}
                      title={isField ? `${cellData?.cell}: ${field?.label || '入力フィールド'}` : val}
                    >
                      {isAutoCheckbox && autoCheckboxId ? (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <input type="checkbox"
                            checked={autoCheckboxChecked}
                            onChange={e => onFieldChange(autoCheckboxId, e.target.checked ? '1' : '0')}
                            style={{ margin: 0, cursor: 'pointer', width: 14, height: 14 }}
                          />
                        </div>
                      ) : isField && fieldId ? (
                        hasResolved ? (
                          <div style={{
                            width: '100%', height: '100%', padding: '2px 4px',
                            display: 'flex', alignItems: 'center', gap: 4, fontSize: st.fontSize ? `${st.fontSize}pt` : '10pt',
                            color: '#1b5e20',
                          }}>
                            <span style={{ fontSize: 9, opacity: 0.6 }}>🔒</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{resolvedVal}</span>
                          </div>
                        ) : isCheckbox ? (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <input type="checkbox"
                              checked={manualVal === '1' || manualVal === '☑' || manualVal === 'true'}
                              onChange={e => onFieldChange(fieldId, e.target.checked ? '1' : '0')}
                              style={{ margin: 0, cursor: 'pointer', width: 16, height: 16 }}
                            />
                          </div>
                        ) : (
                          <input type="text"
                            value={manualVal}
                            onChange={e => onFieldChange(fieldId, e.target.value)}
                            style={{
                              width: '100%', height: '100%', border: 'none', outline: 'none',
                              padding: '2px 4px', background: 'transparent',
                              fontSize: st.fontSize ? `${st.fontSize}pt` : '10pt',
                              fontWeight: st.bold ? 700 : 400,
                              color: '#0d47a1',
                              textAlign: (st.align as 'left' | 'center' | 'right') || 'left',
                              fontFamily: 'inherit',
                            }}
                          />
                        )
                      ) : (
                        val.replace(/þ/g, '☑').replace(/¨/g, '☐')
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
